import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icon";

export { Icon };

const cx = (...a) => a.filter(Boolean).join(" ");

export function Button({ variant = "default", size = "md", block, icon, iconRight, loading, className, children, ...rest }) {
  return (
    <button className={cx("btn", `btn-${variant}`, `btn-${size}`, block && "btn-block", className)} disabled={loading || rest.disabled} {...rest}>
      {loading ? <span className="spin" /> : icon ? <Icon name={icon} size={size === "sm" ? 15 : 17} /> : null}
      {children ? <span>{children}</span> : null}
      {iconRight ? <Icon name={iconRight} size={16} /> : null}
    </button>
  );
}

export function IconButton({ icon, label, active, className, size = 18, ...rest }) {
  return (
    <button type="button" className={cx("icon-btn", active && "is-active", className)} aria-label={label} title={label} {...rest}>
      <Icon name={icon} size={size} />
    </button>
  );
}

export function Field({ label, hint, error, children, right }) {
  return (
    <label className="field">
      {label ? (
        <span className="field-label">
          {label}
          {right}
        </span>
      ) : null}
      {children}
      {error ? <span className="field-error">{error}</span> : hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  );
}

export function Input({ label, hint, error, icon, type = "text", right, className, ...rest }) {
  const [show, setShow] = useState(false);
  const isPw = type === "password";
  return (
    <Field label={label} hint={hint} error={error} right={right}>
      <span className={cx("input-wrap", error && "has-error", className)}>
        {icon ? <Icon name={icon} size={17} className="input-ic" /> : null}
        <input className="input" type={isPw && show ? "text" : type} {...rest} />
        {isPw ? (
          <button type="button" className="input-eye" onClick={() => setShow((s) => !s)} aria-label={show ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}>
            <Icon name={show ? "eyeOff" : "eye"} size={17} />
          </button>
        ) : null}
      </span>
    </Field>
  );
}

export function Textarea({ label, hint, error, className, ...rest }) {
  return (
    <Field label={label} hint={hint} error={error}>
      <textarea className={cx("input textarea", className)} {...rest} />
    </Field>
  );
}

export function Select({ label, hint, options = [], className, ...rest }) {
  return (
    <Field label={label} hint={hint}>
      <span className="input-wrap select-wrap">
        <select className={cx("input", className)} {...rest}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <Icon name="down" size={16} className="select-caret" />
      </span>
    </Field>
  );
}

export function Switch({ checked, onChange, label, disabled }) {
  return (
    <label className={cx("switch", disabled && "is-disabled")}>
      <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange && onChange(e.target.checked)} />
      <span className="switch-track">
        <span className="switch-thumb" />
      </span>
      {label ? <span>{label}</span> : null}
    </label>
  );
}

/** ปุ่มเลือกแบบแบ่งช่อง เช่น รายเดือน | รายปี */
export function Segmented({ value, onChange, options, size = "md" }) {
  return (
    <div className={cx("seg", `seg-${size}`)} role="tablist">
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={value === o.value} className={cx("seg-item", value === o.value && "is-active")} onClick={() => onChange(o.value)}>
          {o.icon ? <Icon name={o.icon} size={15} /> : null}
          {o.label}
          {o.badge ? <span className="seg-badge">{o.badge}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Badge({ tone = "neutral", icon, children, className }) {
  return (
    <span className={cx("badge", `badge-${tone}`, className)}>
      {icon ? <Icon name={icon} size={12} stroke={2.2} /> : null}
      {children}
    </span>
  );
}

export function Card({ className, children, pad = true, ...rest }) {
  return (
    <div className={cx("card", pad && "card-pad", className)} {...rest}>
      {children}
    </div>
  );
}

export function Skeleton({ height = 16, width = "100%", radius = 10, style }) {
  return <span className="skeleton" style={{ height, width, borderRadius: radius, ...style }} />;
}

export function Spinner({ size = 18 }) {
  return <span className="spin" style={{ width: size, height: size }} />;
}

export function Progress({ value = 0, tone = "brand", height = 8 }) {
  const pct = Math.max(0, Math.min(100, value * 100));
  return (
    <span className={cx("progress", `progress-${tone}`)} style={{ height }}>
      <span style={{ width: pct + "%" }} />
    </span>
  );
}

export function EmptyState({ icon = "sparkles", title, children, action }) {
  return (
    <div className="empty">
      <span className="empty-ic">
        <Icon name={icon} size={26} />
      </span>
      <div className="empty-title">{title}</div>
      {children ? <div className="empty-body">{children}</div> : null}
      {action ? <div className="mt-16">{action}</div> : null}
    </div>
  );
}

export function Modal({ open, title, subtitle, onClose, children, footer, size = "md" }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="modal-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose && onClose()}>
      <div className={cx("modal", `modal-${size}`)} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <div>
            <div className="modal-title">{title}</div>
            {subtitle ? <div className="modal-sub">{subtitle}</div> : null}
          </div>
          {onClose ? <IconButton icon="x" label="ปิด" onClick={onClose} /> : null}
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
}

/** โลโก้ MixAI */
export function Logo({ size = 30, text = true }) {
  return (
    <span className="logo">
      <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
        <defs>
          <linearGradient id="mx-g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#ffa149" />
            <stop offset="1" stopColor="#f2540c" />
          </linearGradient>
        </defs>
        <rect width="64" height="64" rx="18" fill="url(#mx-g)" />
        <path d="M16 44V21l16 13 16-13v23" fill="none" stroke="#fff" strokeWidth="6.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="48" cy="15" r="4" fill="#fff" opacity=".9" />
      </svg>
      {text ? (
        <span className="logo-text">
          Mix<b>AI</b>
        </span>
      ) : null}
    </span>
  );
}

/** ป้ายโมเดล — วงกลมสีประจำผู้ให้บริการ + อักษรย่อ (ไม่ใช้โลโก้การค้าจริง) */
export function ModelMark({ model, size = 28 }) {
  const initials = String(model?.name || "?")
    .replace(/[^A-Za-z0-9 ]/g, "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  return (
    <span className="model-mark" style={{ width: size, height: size, background: model?.color || "var(--brand)", fontSize: size * 0.38 }}>
      {initials || "AI"}
    </span>
  );
}

export function Avatar({ name, size = 32 }) {
  const ch = String(name || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.42 }}>
      {ch}
    </span>
  );
}

export function Stat({ label, value, icon, hint, tone = "brand" }) {
  return (
    <div className="stat">
      <div className="stat-top">
        <span className="stat-label">{label}</span>
        {icon ? (
          <span className={cx("stat-ic", `tone-${tone}`)}>
            <Icon name={icon} size={17} />
          </span>
        ) : null}
      </div>
      <div className="stat-value">{value}</div>
      {hint ? <div className="stat-hint">{hint}</div> : null}
    </div>
  );
}

export function Table({ columns, rows, empty = "ยังไม่มีข้อมูล", rowKey = (r) => r.id }) {
  if (!rows?.length) return <div className="table-empty">{empty}</div>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={{ textAlign: c.align || "left", width: c.width }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={rowKey(r)}>
              {columns.map((c) => (
                <td key={c.key} style={{ textAlign: c.align || "left" }} data-label={c.label}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const fmtDate = (iso, withTime = true) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return "-";
  return d.toLocaleString("th-TH", withTime ? { day: "numeric", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" } : { day: "numeric", month: "short", year: "numeric" });
};
